import {Injectable} from '@angular/core';
import {filter, map, tap, last, Observable, switchMap, take, takeWhile, throwError, timeout, timer} from "rxjs";
import {HttpClient, HttpEvent, HttpEventType, HttpResponse, HttpParams} from "@angular/common/http";
import {environment} from '../../environments/environment';
import {CustomButtonToggleOption, GenericResponse, GenericResponseWithPagination} from '../_types/shared';
import {fetchAllPages} from '../_utils/http-params';
import {
    AiGeneration,
    AiGenerationQuery,
    FeedPayload,
    FeedPost,
    NewPost,
    Post,
    PostAttachmentType,
    PostComment,
    PostCommentLikeResponse,
    PostLikeResponse,
    PostNewComment,
    SimplifiedPost,
    Theme,
    UpdatePost
} from '../_types/content';
import {jsonToFormData} from '../_utils/form-data-builder';
import {LocalStorageService} from './local-storage.service';
import {removeNullUndefinedFields} from "../_utils/remove-null-fields";

@Injectable({
    providedIn: 'root'
})
export class ContentService {

    constructor(private http: HttpClient,
                private localStorageService: LocalStorageService) {
    }

    searchGifs(query: string, limit: number, offset?: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('api_key', 'zsidsAPTg0trCoXmjc0NtEKJmTml3Zff');
        parameters = parameters.set('q', query);
        parameters = parameters.set('rating', 'g');
        parameters = parameters.set('limit', limit);
        if (offset) {
            parameters = parameters.set('offset', offset * limit);
        }
        return this.http.get<any>(`https://api.giphy.com/v1/gifs/search`, {
            params: parameters
        });
    }

    getPosts(page: number, perPage = 10, filters: {
        is_pinned_to_sidebar?: boolean,
        community?: number,
        search?: string,
    } = {}): Observable<GenericResponseWithPagination<Post>> {
        const payload = {
            ...filters,
            page: page,
            page_size: perPage,
        }
        return this.http.get<GenericResponseWithPagination<Post>>(`${environment.apiUrl}/posts/`, {params: payload});
    }

    getFeed(communityId: number, page: number, perPage = 10, filters: FeedPayload = {}): Observable<GenericResponseWithPagination<FeedPost>> {
        const payload = {
            ...filters,
            page: page,
            page_size: perPage,
        }
        return this.http.get<GenericResponseWithPagination<FeedPost>>(`${environment.apiUrl}/${communityId}/feed/`, {params: payload});
    }

    getSimplifiedPosts(page: number, perPage = 10, filters: FeedPayload = {}): Observable<GenericResponseWithPagination<SimplifiedPost>> {
        const payload = {
            ...filters,
            page: page,
            page_size: perPage,
        }
        return this.http.get<GenericResponseWithPagination<SimplifiedPost>>(`${environment.apiUrl}/posts/summary/`, {params: payload});
    }

    getRecentActivity(communityId: number, limit: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();
        if (communityId) {
            parameters = parameters.set('community', communityId);
        }
        if (limit) {
            parameters = parameters.set('limit', limit);
        }
        return this.http.get<any>(`${environment.apiUrl}/activity/recent`, {params: parameters});
    }

    newPost(postData: NewPost, onProgress?: (loaded: number, total?: number) => void): Observable<Post> {
        let formData: any;
        const files = postData.attachment_files
        const images = postData.images;
        delete (postData as any).images;
        delete (postData as any).attachment_files;

        formData = postData;
        if (formData.subgroups) {
            formData.subgroups = JSON.stringify(formData.subgroups).replace(/[\[\]']+/g, '');
        }
        if (formData.categories) {
            formData.categories = JSON.stringify(formData.categories).replace(/[\[\]']+/g, '');
        }
        formData = jsonToFormData(postData);
        if (files) {
            for (let file of files) {
                formData.append('attachment_files', file)
            }
        }
        if (images?.length) {
            for (const img of images) {
                formData.append('images', img);
            }
        }
        if (onProgress) {
            return this.observePostUpload(this.http.post<Post>(`${environment.apiUrl}/posts/`, formData,
                {observe: 'events', reportProgress: true}), onProgress);
        }
        return this.http.post<Post>(`${environment.apiUrl}/posts/`, formData);
    }

    updatePost(postId: number, postData: UpdatePost, notFormData?: boolean, queryParams?: any, onProgress?: (loaded: number, total?: number) => void): Observable<Post> {
        let formData: any;
        let parameters: HttpParams = new HttpParams();
        if (queryParams?.remove_subgroups) {
            parameters = parameters.set('remove_subgroups', 1);
        }
        if (queryParams?.unlock_post) {
            parameters = parameters.set('unlock_post', 1);
        }

        if (notFormData) {
            return this.http.patch<Post>(`${environment.apiUrl}/posts/${postId}/`, postData, {params: parameters});
        } else {
            const files = postData.attachment_files
            const images = postData.images;
            delete (postData as any).images;
            delete (postData as any).attachment_files;


            formData = postData;
            if (formData.subgroups) {
                formData.subgroups = JSON.stringify(formData.subgroups).replace(/[\[\]']+/g, '');
            }
            if (formData.categories) {
                formData.categories = JSON.stringify(formData.categories).replace(/[\[\]']+/g, '');
            }

            formData = jsonToFormData(postData);
            if (files) {
                for (let file of files) {
                    formData.append('attachment_files', file)
                }
            }
            if (images?.length) {
                for (const img of images) {
                    formData.append('images', img);
                }
            }
            if (onProgress) {
                return this.observePostUpload(this.http.patch<Post>(`${environment.apiUrl}/posts/${postId}/`, formData,
                    {params: parameters, observe: 'events', reportProgress: true}), onProgress);
            }
            return this.http.patch<Post>(`${environment.apiUrl}/posts/${postId}/`, formData, {params: parameters});
        }
    }

    private observePostUpload(events: Observable<HttpEvent<Post>>, onProgress: (loaded: number, total?: number) => void): Observable<Post> {
        return events.pipe(
            tap(event => {
                if (event.type === HttpEventType.UploadProgress) onProgress(event.loaded, event.total);
            }),
            filter((event): event is HttpResponse<Post> => event instanceof HttpResponse),
            map(event => event.body!),
        );
    }

    deletePost(postId: number): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/posts/${postId}/`);
    }

    getPostById(id: number): Observable<Post> {
        return this.http.get<Post>(`${environment.apiUrl}/posts/${id}/`);
    }

    likePost(postId: number): Observable<GenericResponse<PostLikeResponse>> {
        return this.http.post<GenericResponse<PostLikeResponse>>(`${environment.apiUrl}/posts/${postId}/likes/`, {});
    }

    unlikePost(postId: number): Observable<GenericResponse<PostLikeResponse>> {
        return this.http.delete<GenericResponse<PostLikeResponse>>(`${environment.apiUrl}/posts/${postId}/likes/`);
    }

    dislikePost(postId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/dislikes/`, {});
    }

    unDislikePost(postId: number,): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/dislikes/`);
    }

    getPostComments(postId: number): Observable<PostComment[]> {
        const sort = this.localStorageService.getItem<CustomButtonToggleOption | null>('commentsSortOption', null);
        const params = new HttpParams().set('order_by', sort?.value === 'new' ? 'newest-oldest' : 'oldest-newest');
        return fetchAllPages<PostComment>(this.http, `${environment.apiUrl}/posts/${postId}/comments/`, 1000, params);
    }

    getAllCommentsModerationComments(page: number, limit: number, params: any): Observable<any> {
        let parameters: HttpParams = new HttpParams();
        if (limit) {
            parameters = parameters.set('page_size', limit);
        }
        if (page) {
            parameters = parameters.set('page', page);
        }

        Object.keys(params).forEach(key => {
            const value: any = params[key];
            // Only set the parameter if the value is not null or undefined
            if (value !== null && value !== undefined) {
                parameters = parameters.set(key, value);
            }
        });
        return this.http.get<any>(`${environment.apiUrl}/admin/comments/`, {params: parameters});
    }

    getAllComments(page: number, limit: number, community?: number, user?: number): Observable<any> {
        let parameters: HttpParams = new HttpParams();


        if (community) {
            parameters = parameters.set('community', community);
        }
        if (user) {
            parameters = parameters.set('user', user);
        }
        if (limit) {
            parameters = parameters.set('page_size', limit);
        }
        if (page) {
            parameters = parameters.set('page', page);
        }
        return this.http.get<any>(`${environment.apiUrl}/comments/`, {params: parameters});
    }

    // getPostB(id: number): Observable<Post> {
    //     return this.http.get<Post>(`${environment.apiUrl}/posts/${id}/`);
    // }

    newPostComments(postId: number, type: PostAttachmentType | 'text', comment: PostNewComment): Observable<GenericResponse<PostComment>> {
        let formData: FormData;

        if (type === 'text' || type === 'video_url') {
            return this.http.post<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/`, comment);
        } else {
            formData = jsonToFormData(comment);
            return this.http.post<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/`, formData);
        }
    }

    deletePostComments(postId: number, commentId: number): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`);
    }

    reportPostComments(postId: number, commentId: number): Observable<void> {
        return this.http.get<void>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/flag-for-review/`);
    }

    likeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/likes/`, {});
    }

    unlikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/likes/`);
    }

    dislikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.post<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/dislikes/`, {});
    }

    unDislikeComment(postId: number, commentId: number): Observable<GenericResponse<PostCommentLikeResponse>> {
        return this.http.delete<GenericResponse<PostCommentLikeResponse>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/dislikes/`);
    }

    patchComment(postId: number, commentId: number, body: Partial<PostComment>, type: PostAttachmentType | 'text' = 'text'): Observable<GenericResponse<PostComment>> {
        let formData: FormData;

        if (type === 'text' || type === 'video_url') {
            return this.http.patch<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`, body);
        } else {
            formData = jsonToFormData(body);
            return this.http.patch<GenericResponse<PostComment>>(`${environment.apiUrl}/posts/${postId}/comments/${commentId}/`, formData);
        }
    }

    exportComments(postId: number, themeId?: number): Observable<string> {
        let parameters: HttpParams = new HttpParams();
        if (themeId) {
            parameters = parameters.set('theme', themeId);
        }
        return this.http.get(`${environment.apiUrl}/posts/${postId}/export-comments/`, {
            params: parameters,
            responseType: 'text'
        });
    }

    // Themes
    getPostThemes(postId: number): Observable<GenericResponseWithPagination<Theme>> {
        return this.http.get<GenericResponseWithPagination<Theme>>(`${environment.apiUrl}/post-themes/?post=${postId}`);
    }

    postTheme(postId: number, name: string): Observable<GenericResponse<Theme>> {
        return this.http.post<GenericResponse<Theme>>(`${environment.apiUrl}/post-themes/`, {post: postId, name: name});
    }

    deleteTheme(themeId: number): Observable<any> {
        return this.http.delete<any>(`${environment.apiUrl}/post-themes/${themeId}/`);
    }

    // other
    downloadCSV(url: string, filename: string = 'downloaded_file.csv') {
        this.http.get(url, {responseType: 'blob'}).subscribe((blob) => {
            // Create a new Blob object and download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        });
    }

    async fetchFile(url: string): Promise<any> {
        let response: Response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-store',
        });
        let data: Blob = await response.blob();
        const regex = /([^\/?#]+)\.([a-zA-Z0-9]+)(?=[?#]|$)/;
        const fileName = url?.match(regex)?.[0] || 'image.jpg';
        return new File([data], fileName, {type: data.type});
    }

    reportAction(action: string, model: string, postId?: number | null, communityId?: number | null, metadata?: string | null): Observable<any> {
        let body = {
            "model_name": model,
            "action": action,
            "object_id": postId,
            "community": communityId,
            "metadata": metadata
        }
        return this.http.post(`${environment.apiUrl}/tracked-actions/`, removeNullUndefinedFields(body));
    }

    postSurveyComplete(postId: number) {
        return this.http.post(`${environment.apiUrl}/survey-status/` + postId + '/', {});
    }

    getSurveyStatus(surveyId: number): Observable<any> {
        return this.http.get(`${environment.apiUrl}/survey-status/` + surveyId + '/');
    }

    //  Ai

    generatePostFromPrompt(community_id: number, prompt: string): Observable<AiGeneration> {
        return this.http.post<AiGeneration>(`${environment.apiUrl}/ai-generations/`, {
            community_id,
            prompt
        });
    }

    queryAiGenerationJob(jobId: string): Observable<AiGenerationQuery> {
        return this.http.get<AiGenerationQuery>(`${environment.apiUrl}/ai-generations/` + jobId + '/');
    }

    generateAIPostContentAndListenForResponse(
        community_id: number,
        prompt: string,
        opts?: {
            pollEveryMs?: number;
            maxPolls?: number;
            overallTimeoutMs?: number;
        }
    ): Observable<AiGenerationQuery> {
        const pollEveryMs: number = opts?.pollEveryMs ?? 1500;
        const maxPolls: number = opts?.maxPolls ?? 120;
        const overallTimeoutMs: number = opts?.overallTimeoutMs ?? 5 * 60_000;

        return this.generatePostFromPrompt(community_id, prompt).pipe(
            switchMap((created: AiGeneration): Observable<AiGenerationQuery> => {
                const jobId = created?.data?.job_id;
                if (!jobId) {
                    return throwError(() => new Error('AI generation did not return data.job_id'));
                }

                return timer(0, pollEveryMs).pipe(
                    take(maxPolls),
                    switchMap((): Observable<AiGenerationQuery> => this.queryAiGenerationJob(jobId)),
                    takeWhile((q: AiGenerationQuery) => !this.isAiQueryTerminal(q), true),
                    last(),
                    timeout({first: overallTimeoutMs, each: overallTimeoutMs})
                );
            })
        );
    }

    createAIPollSummaryJob(user_id: number, poll_id: number): Observable<AiGeneration> {
        return this.http.post<AiGeneration>(`${environment.apiUrl}/polls/${poll_id}/ai-comment-draft/`, {
            user_id,
        });
    }

    queryAIPollSummaryJob(jobId: string): Observable<AiGenerationQuery> {
        return this.http.get<AiGenerationQuery>(`${environment.apiUrl}/ai-comment-drafts/${jobId}/`);
    }

    generateAIPollSummaryAndListenForResponse(
        user_id: number,
        poll_id: number,
        opts?: {
            pollEveryMs?: number;
            maxPolls?: number;
            overallTimeoutMs?: number;
        }
    ): Observable<AiGenerationQuery> {
        const pollEveryMs: number = opts?.pollEveryMs ?? 1500;
        const maxPolls: number = opts?.maxPolls ?? 120;
        const overallTimeoutMs: number = opts?.overallTimeoutMs ?? 5 * 60_000;

        return this.createAIPollSummaryJob(user_id, poll_id).pipe(
            switchMap((created: AiGeneration): Observable<AiGenerationQuery> => {
                const jobId = created?.data?.job_id;
                if (!jobId) {
                    return throwError(() => new Error('AI generation did not return data.job_id'));
                }

                return timer(0, pollEveryMs).pipe(
                    take(maxPolls),
                    switchMap((): Observable<AiGenerationQuery> => this.queryAIPollSummaryJob(jobId)),
                    takeWhile((q: AiGenerationQuery) => !this.isAiQueryTerminal(q), true),
                    last(),
                    timeout({first: overallTimeoutMs, each: overallTimeoutMs})
                );
            })
        );
    }

    private isAiQueryTerminal(query: AiGenerationQuery): boolean {
        const result: string | null | undefined = query?.data?.result;
        const error: string | null | undefined = query?.data?.error;

        if (error && error.trim().length > 0) return true;
        if (result && result.trim().length > 0) return true;

        if (query.data?.status == 'succeeded') return true;

        return false;
    }

}
